import { useCallback, useEffect, useMemo, useState } from "react";

import { Column, SkeletonRounded, Text, Row, TagTextAlert } from "@bridgesplit/ui";
import {
    calculateLeverageMultiplier,
    getZcCollateral,
    getZcLedger,
    AbfLoanExpanded,
    LoopPositionExpanded,
    useLoopscaleWalletContext,
    getZcLoanCollateral,
    StrategyExpandedInfo,
    getLoopSlippage
} from "@bridgesplit/abf-react";
import { lamportsToUiAmount, LOADING_ERROR, MISSING_WALLET_ERROR, Result } from "@bridgesplit/utils";
import {
    AppButton,
    Slippage,
    formatSlippageRowEndText,
    PandoraQuoteError,
    RefreshButton,
    useLoanOraclePriceQuotes
} from "app/components/common";
import { COPY, TEST_IDS } from "app/constants";
import LoopLeverageSlider from "app/components/loop/wind/LoopLeverageSlider";
import { roundAndClamp, useLoopWindCallback } from "app/components/loop/wind/util";
import { getDurationIndex } from "@bridgesplit/abf-sdk";
import { SwapInstructionsResponse, WindType } from "@loopscale/pandora-types";
import { ExecutionDetailsLabels, useExecutionDetails } from "app/components/loop/execution-detail";
import debounce from "lodash.debounce";
import { AppDialog, useAppDialog } from "app/utils/dialog";

import { createRepaymentParams, useRepay } from "./util";
import {
    ManageLeverageAction,
    getExecutionDetailsInputs,
    useCurrentStrategyLeverageCapacity,
    // useGetStrategyStats,
    calculateNewDebtAndCollateralAmountsFromQuote,
    useManageLeverageQuotes,
    useCalculatePrincipalLeverageAdjustment
} from "./hooks/manageLeverageHooks";
import { ManageLeverageForm } from "./types";
import { ActionsSettingsButton } from "../common";
import { LoanPositionStats } from "../LoanPositionStats";

export function AutoManageLeverageSliderCard({
    loopPosition,
    loanExpanded,
    dialogView = false
}: {
    loopPosition: LoopPositionExpanded | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    dialogView?: boolean;
}) {
    const { defaultSlippageUi, slippagePresetsUi } = getLoopSlippage(loopPosition?.loopExpanded);
    const slippageController = Slippage.useController({
        defaultSlippageUi,
        presetsUi: slippagePresetsUi
    });

    return (
        <Slippage.Wrapper {...slippageController.slippageWrapperProps}>
            <AutoManageLeverageSlider
                loopPosition={loopPosition}
                loanExpanded={loanExpanded}
                dialogView={dialogView}
                slippageController={slippageController}
            />
        </Slippage.Wrapper>
    );
}

export function AutoManageLeverageSlider({
    loopPosition,
    loanExpanded,
    dialogView = false,
    slippageController
}: {
    loopPosition: LoopPositionExpanded | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
    dialogView?: boolean;
    slippageController: ReturnType<typeof Slippage.useController>;
}) {
    const [form, setForm] = useState<ManageLeverageForm>({
        multiplier: undefined,
        increasePrincipalAmount: undefined,
        repayWithCollateralAmount: undefined
    });
    const { open } = useAppDialog();
    const startingLeverage = calculateLeverageMultiplier(loanExpanded);

    const [multiplier, setMultiplier] = useState(startingLeverage);
    const [multiplierDebounced, setMultiplierDebounced] = useState(startingLeverage);

    const { principalUsdOraclePrice, collateralUsdOraclePrice } = useLoanOraclePriceQuotes(
        loanExpanded?.principalMetadata,
        getZcCollateral(loanExpanded)
    );

    const collateral = getZcCollateral(loanExpanded);

    const { data: leverageCapacity, isLoading: capacityLoading } = useCurrentStrategyLeverageCapacity(
        loanExpanded,
        startingLeverage
    );

    const maxAvailableLeverage = leverageCapacity?.maxAvailableLeverage;
    const increaseLeverageApy = leverageCapacity?.increaseLeverageApy;
    const increaseLeverageLiquidationThreshold = leverageCapacity?.increaseLeverageLiquidationThreshold;
    const increaseLeverageLtv = leverageCapacity?.strategyCurrentLtv;
    const strategy = leverageCapacity?.strategy;

    const action = useMemo(() => {
        if (multiplierDebounced === undefined || startingLeverage === undefined || multiplierDebounced === 1) {
            return ManageLeverageAction.NoAction;
        }

        // Round to 0.1 precision for comparison (same as hasLeverageChange)
        const roundedMultiplier = Math.round(multiplierDebounced * 10) / 10;
        const roundedStarting = Math.round(startingLeverage * 10) / 10;

        if (roundedMultiplier > roundedStarting) {
            return ManageLeverageAction.IncreaseLeverage;
        }

        if (roundedMultiplier < roundedStarting) {
            return ManageLeverageAction.DecreaseLeverage;
        }

        return ManageLeverageAction.NoAction;
    }, [multiplierDebounced, startingLeverage]);

    const {
        quote,
        isLoading: quoteLoading,
        isFetching: quoteFetching,
        error: quoteError,
        validHealth
    } = useManageLeverageQuotes({
        action,
        loanExpanded,
        loopPosition,
        slippagePercentDecimals: slippageController.slippagePercentDecimals,
        increasePrincipalAmount: form.increasePrincipalAmount,
        repayWithCollateralAmount: form.repayWithCollateralAmount
    });

    const executionDetailsInputs = getExecutionDetailsInputs({
        principalMetadata: loanExpanded?.principalMetadata,
        increaseLeverageQuote: action === ManageLeverageAction.IncreaseLeverage ? quote : undefined,
        increasePrincipalAmount: form.increasePrincipalAmount,
        repayWithCollateralQuote: action === ManageLeverageAction.DecreaseLeverage ? quote : undefined,
        repayWithCollateralAmount: form.repayWithCollateralAmount,
        collateral,
        isIncreaseLeverage: action === ManageLeverageAction.IncreaseLeverage,
        principalUsdPrice: principalUsdOraclePrice,
        collateralUsdPrice: collateral?.usdPrice
    });

    const handleChange = useCallback((multiplier: number) => setMultiplier(multiplier), [setMultiplier]);

    const setValueDebounced = useMemo(
        () =>
            debounce((val: number) => {
                setMultiplierDebounced(roundAndClamp(val, maxAvailableLeverage ?? 0));
            }, 500),
        [setMultiplierDebounced, maxAvailableLeverage]
    );
    // Use the handler to determine borrow/repay amounts
    const adjustment = useCalculatePrincipalLeverageAdjustment(
        multiplierDebounced,
        startingLeverage,
        loanExpanded,
        principalUsdOraclePrice,
        collateralUsdOraclePrice
    );

    const principalBorrowAmount = adjustment?.principalBorrowAmount;
    const collateralAmountToRepay = adjustment?.collateralAmountToRepay;

    const isDecreaseLeverage = collateralAmountToRepay && collateralAmountToRepay > 0;

    useEffect(() => {
        setForm((prev) => ({
            ...prev,
            multiplier: multiplierDebounced,
            increasePrincipalAmount: !isDecreaseLeverage ? principalBorrowAmount : undefined,
            repayWithCollateralAmount: !isDecreaseLeverage ? undefined : collateralAmountToRepay,
            slippagePercentDecimals: slippageController.slippagePercentDecimals
        }));
    }, [
        multiplierDebounced,
        isDecreaseLeverage,
        principalBorrowAmount,
        collateralAmountToRepay,
        setForm,
        slippageController.slippagePercentDecimals
    ]);

    const hasLeverageChange = action !== ManageLeverageAction.NoAction;
    const currentDebtAmount = getZcLedger(loanExpanded)?.ledgerDebt.total ?? 0;
    const currentCollateralAmount = getZcCollateral(loanExpanded)?.amount ?? 0;
    const { newDebtAmount, newCollateralAmount } = useMemo(
        () =>
            calculateNewDebtAndCollateralAmountsFromQuote({
                loanExpanded,
                isAddingLeverage: action === ManageLeverageAction.IncreaseLeverage,
                quote,
                liquidationThreshold:
                    action === ManageLeverageAction.IncreaseLeverage ? increaseLeverageLiquidationThreshold : undefined
            }),
        [loanExpanded, action, quote, increaseLeverageLiquidationThreshold]
    );

    const ctaText = useMemo(() => {
        if (action === ManageLeverageAction.NoAction) return "Adjust leverage";
        if (action === ManageLeverageAction.DecreaseLeverage) return "Decrease leverage";
        return "Increase leverage";
    }, [action]);

    const ctaAlert = useMemo(() => {
        if (!validHealth) {
            return (
                <TagTextAlert color="warning" icon="warning">
                    Invalid health for {action === ManageLeverageAction.IncreaseLeverage ? "increase" : "decrease"}{" "}
                    leverage quote
                </TagTextAlert>
            );
        }
        if (quoteError && typeof quoteError !== "boolean" && action !== ManageLeverageAction.NoAction) {
            return <PandoraQuoteError externalQuoteError={quoteError} />;
        }

        return null;
    }, [quoteError, action, validHealth]);

    const manageLeverageHook = useManageLeverage({
        loanExpanded,
        loopPosition,
        increaseLeverageApy,
        strategy,
        repayWithCollateralQuote: action === ManageLeverageAction.DecreaseLeverage ? quote : undefined,
        increaseLeverageQuote: action === ManageLeverageAction.IncreaseLeverage ? quote : undefined,
        form
    });

    const { manageLeverage } = manageLeverageHook;
    const isLoading = capacityLoading || quoteLoading;
    const isRefetching = quoteFetching && !quoteLoading;

    const executionDetails = useExecutionDetails({
        slippagePercentDecimals: slippageController.slippagePercentDecimals,
        externalQuote: executionDetailsInputs?.externalQuote,
        receiveUsdAmount: executionDetailsInputs?.receiveUsdAmount,
        fairMarketValue: executionDetailsInputs?.fairMarketValue,
        labelOverrides: getLabelOverridesForManageLeverage(executionDetailsInputs?.displayTokenType),
        principalUsdPrice: principalUsdOraclePrice,
        collateralUsdPrice: collateralUsdOraclePrice,
        principalMetadata: loanExpanded?.principalMetadata,
        collateralMetadata: getZcCollateral(loanExpanded)?.metadata
    });

    if (loanExpanded && !isLoading && !leverageCapacity) {
        return (
            <Column spacing={2}>
                <Text variant="h4">{COPY.MANAGE_LEVERAGE_TERM}</Text>
                <TagTextAlert color="warning" icon="warning">
                    Manage leverage not supported for this position. Please manage your position manually.
                </TagTextAlert>
            </Column>
        );
    }

    return (
        <Column spacing={2}>
            {capacityLoading || maxAvailableLeverage === undefined ? (
                <SkeletonRounded width="100%" height={75} />
            ) : (
                <Column spacing={0}>
                    <Row spaceBetween>
                        <Row>
                            <Text variant="body2" color="caption">
                                {COPY.LEVERAGE_TERM}
                            </Text>
                            <RefreshButton
                                textColor="secondary"
                                textVariant="body2"
                                disabled={isLoading}
                                border={false}
                                refresh={() => {
                                    setMultiplier(startingLeverage);
                                    setMultiplierDebounced(startingLeverage);
                                }}
                            />
                        </Row>

                        <ActionsSettingsButton
                            rowEndText={formatSlippageRowEndText(
                                slippageController.slippagePercentUi,
                                slippageController.slippagePercentDecimals
                            )}
                            onClick={() => {
                                if (dialogView) {
                                    return slippageController.slippageButtonProps.open();
                                }
                                return open(AppDialog.SlippageSettings, slippageController.slippageDialogProps);
                            }}
                        />
                    </Row>
                    <LoopLeverageSlider
                        multiplierWithoutDebounce={multiplier}
                        maxLeverageValue={maxAvailableLeverage}
                        onMultiplierWithoutDebounceChange={handleChange}
                        onMultiplierDebouncedChange={setValueDebounced}
                    />
                </Column>
            )}

            {ctaAlert
                ? ctaAlert
                : (dialogView || action !== ManageLeverageAction.NoAction) && (
                      <Column
                          spacing={0.5}
                          sx={{
                              opacity: isRefetching ? 0.5 : 1,
                              transition: "opacity 0.2s ease"
                          }}
                      >
                          <LoanPositionStats
                              loanExpanded={loanExpanded}
                              loopPosition={loopPosition}
                              newPrincipalAmount={hasLeverageChange ? newDebtAmount ?? 0 : currentDebtAmount}
                              newCollateralAmount={
                                  hasLeverageChange ? newCollateralAmount ?? 0 : currentCollateralAmount
                              }
                              isFullRepay={false}
                              loading={isLoading}
                              textVariant="body2"
                              executionDetails={executionDetails}
                              newBorrowApy={
                                  action === ManageLeverageAction.IncreaseLeverage ? increaseLeverageApy : undefined
                              }
                              newStrategyLtv={
                                  action === ManageLeverageAction.IncreaseLeverage ? increaseLeverageLtv : undefined
                              }
                              newLiquidationThreshold={
                                  action === ManageLeverageAction.IncreaseLeverage
                                      ? increaseLeverageLiquidationThreshold
                                      : undefined
                              }
                              startingLeverage={startingLeverage}
                              newLeverage={multiplierDebounced}
                              show={{
                                  debtAndCollateral: false,
                                  leverageCollapsable: true,
                                  health: true,
                                  netApyCollapsable: true,
                                  borrowApy: false,
                                  executionDetails: true
                              }}
                          />
                      </Column>
                  )}

            <AppButton
                data-testid={TEST_IDS.MANAGE_LEVERAGE_BUTTON}
                isTransaction
                disabled={action === ManageLeverageAction.NoAction || isLoading || quoteFetching || !!ctaAlert}
                asyncCta={{ onClickWithResult: manageLeverage, closeDialog: "onSuccess" }}
                fullWidth
            >
                {ctaText}
            </AppButton>
        </Column>
    );
}

function useManageLeverage({
    loanExpanded,
    loopPosition,
    increaseLeverageApy,
    strategy,
    repayWithCollateralQuote,
    increaseLeverageQuote,
    form
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    increaseLeverageApy: number | undefined;
    strategy: StrategyExpandedInfo | undefined;
    repayWithCollateralQuote: SwapInstructionsResponse | undefined;
    increaseLeverageQuote: SwapInstructionsResponse | undefined;
    form: ManageLeverageForm;
}) {
    const collateral = getZcLoanCollateral(loanExpanded);
    const ledger = getZcLedger(loanExpanded);

    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    // Determine leverage operation type
    const isIncreaseLeverage = form.repayWithCollateralAmount === undefined;

    // Repayment params
    const params = createRepaymentParams({
        principalPaymentUiAmount: lamportsToUiAmount(
            repayWithCollateralQuote?.quote.minOutputAmount,
            loanExpanded?.principalMetadata.decimals
        ),
        collateralPaymentUiAmount: lamportsToUiAmount(
            repayWithCollateralQuote?.quote.inputAmount,
            loopPosition?.loopExpanded?.collateralToken.decimals
        ),
        isFullRepay: false, // cant repay via manage leverage slider
        repayType: WindType.Collateral,
        collateral,
        loanExpanded,
        quote: repayWithCollateralQuote
    });

    const repayHook = useRepay(params);

    const loopWindCallback = useLoopWindCallback({
        loanExpanded,
        loopExpanded: loopPosition?.loopExpanded,
        externalQuote: increaseLeverageQuote,
        collateralAmountToDeposit: lamportsToUiAmount(
            increaseLeverageQuote?.quote.minOutputAmount,
            loopPosition?.loopExpanded?.collateralToken.decimals
        ),
        multiplier: form.multiplier,
        percentSlippageDecimals: 0,
        loanCreationParams: "Default",
        bestQuoteLendingStrategyAddress: strategy?.strategy.address ?? "",
        bestQuoteApy: increaseLeverageApy ?? 0,
        bestQuoteLqt: strategy?.collateral.find((c) => c.metadata.mint === collateral?.assetMint)?.ltvInfo
            .liquidationThreshold,
        principalAmountRequested: form.increasePrincipalAmount,
        durationIndex:
            ledger?.duration !== undefined && ledger?.durationType !== undefined
                ? getDurationIndex({
                      duration: ledger.duration,
                      durationType: ledger.durationType
                  })
                : undefined
    });

    const manageLeverageTransaction = useCallback(async () => {
        if (!loanExpanded) return Result.errFromMessage(LOADING_ERROR);
        if (!activeWallet) return Result.errFromMessage(MISSING_WALLET_ERROR);

        if (!isIncreaseLeverage) {
            const result = await repayHook.repay();
            return result;
        }
        return await loopWindCallback({
            collateralMint: loopPosition?.loopExpanded?.collateralToken?.mint,
            inputMint: loanExpanded.principalMetadata.mint
        });
    }, [loanExpanded, activeWallet, isIncreaseLeverage, repayHook, loopWindCallback, loopPosition]);

    return {
        manageLeverage: () => manageLeverageTransaction()
    };
}

function getLabelOverridesForManageLeverage(windType: WindType | undefined): Partial<ExecutionDetailsLabels> {
    const isDecreaseLeverage = windType === WindType.Principal;

    return {
        executionImpact: {
            caption: "Execution impact",
            tooltip: isDecreaseLeverage
                ? `Combined worst-case impact of slippage, fees, price impact, and market price for swapping loan collateral to repay principal`
                : `Combined worst-case impact of slippage, fees, price impact, and market price for swapping principal for collateral`
        },
        fairMarketValue: {
            caption: "Fair market value",
            tooltip: isDecreaseLeverage
                ? `The fair market oracle value of principal from swapped collateral`
                : `The fair market oracle value of collateral from swapped principal`
        },
        oracleSpread: {
            caption: "Oracle spread",
            tooltip: `Adjustment due to difference in oracle price and swap price`
        },
        receiveOnClose: {
            caption: isDecreaseLeverage ? "Principal Repaid" : "Collateral Deposited",
            tooltip: isDecreaseLeverage
                ? `The minimum amount of repaid principal after swapping position collateral`
                : `The minimum amount of collateral deposited after swapping position principal`
        }
    };
}
